import { Link } from "../lib/route";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Hero from "../components/Hero";
import TrustBar from "../components/TrustBar";
import ServiceCard from "../components/ServiceCard";
import WhyChooseUs from "../components/WhyChooseUs";
import ProcessSteps from "../components/ProcessSteps";
import TestimonialCard from "../components/TestimonialCard";
import ServiceArea from "../components/ServiceArea";
import CTABanner from "../components/CTABanner";
import ConsultationForm from "../components/ConsultationForm";
import { IMAGES, SERVICES, TESTIMONIALS, COPY, withElStyle } from "../lib/_data";

export default function Home() {
  const heroCopy = COPY.home.hero;
  const svcCopy = COPY.home.servicesOverview;
  const consultCopy = COPY.home.consultation;
  const testiCopy = COPY.home.testimonials;
  const finalCta = COPY.home.finalCta;
  const consultBullets = (consultCopy.bullets && consultCopy.bullets.length ? consultCopy.bullets : [
    "No-obligation diagnosis at your home",
    "Written, itemized quote — no surprise fees",
    "Same-week scheduling for most services",
    "Talk to a real human, not a call center",
  ]).map((b, i) => consultCopy[`bullet${i + 1}`] || b);
  return (
    <div data-testid="page-home">
      <Hero
        image={IMAGES.homeHero}
        eyebrow={heroCopy.eyebrow || "Local · Licensed · Insured"}
        title={heroCopy.title || "Trusted home services, done right the first time."}
        subtitle={heroCopy.subtitle || "From a leaky faucet to a full kitchen remodel — one local team you can actually rely on. Free consultations, honest quotes, workmanship warranty on every job."}
        primaryCta={{ label: heroCopy.primaryCtaLabel || "Book a Consultation", to: heroCopy.primaryCtaTo || "/consultations" }}
        secondaryCta={{ label: heroCopy.secondaryCtaLabel || "View Services", to: heroCopy.secondaryCtaTo || "/services" }}
        trustLine={heroCopy.trustLine || "Serving local homeowners and businesses since 2011"}
      />
      <TrustBar />

      {/* Services overview */}
      <section
        {...withElStyle("services-overview", { className: "bg-[#f9f6f0] py-20 md:py-32" })}
        data-testid="services-overview"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <span className="text-xs tracking-[0.24em] uppercase font-bold text-[#c85a3c]" data-testid="home-services-eyebrow">
                {svcCopy.eyebrow || "What we do"}
              </span>
              <h2 className="font-display text-3xl md:text-5xl font-black text-[#2c2a29] leading-[1.05] mt-4" data-testid="home-services-title">
                {svcCopy.title || "Every service your home needs, under one roof."}
              </h2>
              <p className="mt-5 text-[#5c5855] leading-relaxed" data-testid="home-services-subtitle">
                {svcCopy.subtitle || "Skip juggling contractors. One licensed team handles the small repairs and the big projects — with the same care either way."}
              </p>
            </div>
            <Link
              to={svcCopy.viewAllTo || "/services"}
              data-testid="services-view-all"
              className="inline-flex items-center gap-2 text-[#2c2a29] font-bold hover:text-[#c85a3c] transition-colors"
            >
              {svcCopy.viewAllLabel || "View all services"}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
            {SERVICES.map((s) => (
              <ServiceCard key={s.slug} service={s} />
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link
              to={svcCopy.quoteCtaTo || "/consultations"}
              data-testid="services-cta"
              className="inline-flex items-center gap-2 bg-[#2c2a29] hover:bg-[#c85a3c] text-[#fffdf9] font-bold px-7 py-4 rounded-full transition-all hover:-translate-y-0.5"
            >
              {svcCopy.quoteCtaLabel || "Request a Free Quote"}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <WhyChooseUs />

      {/* Consultation sell + inline form */}
      <section
        data-testid="consultation-sell"
        className="relative bg-[#f9f6f0] py-20 md:py-32 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div className="lg:sticky lg:top-32">
            <span className="text-xs tracking-[0.24em] uppercase font-bold text-[#c85a3c]" data-testid="home-consultation-eyebrow">
              {consultCopy.eyebrow || "Free consultations"}
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-black text-[#2c2a29] leading-[1.05] mt-4" data-testid="home-consultation-title">
              {consultCopy.title || "Not sure what you need? Let's figure it out together."}
            </h2>
            <p className="mt-6 text-[#5c5855] leading-relaxed" data-testid="home-consultation-subtitle">
              {consultCopy.subtitle || "Most home projects start with a \"hmm, I think something's wrong.\" Book a free consultation and we'll come out, diagnose the real issue, and walk you through every option — without pressure or upsells."}
            </p>
            <ul className="mt-8 space-y-4">
              {consultBullets.map((c, i) => (
                <li key={c} className="flex items-start gap-3" data-testid={`consult-bullet-${i + 1}`}>
                  <CheckCircle2 className="w-5 h-5 text-[#849982] mt-0.5 flex-shrink-0" />
                  <span className="text-[#2c2a29] font-medium">{c}</span>
                </li>
              ))}
            </ul>
          </div>

          <div id="consult">
            <ConsultationForm />
          </div>
        </div>
      </section>

      <ProcessSteps />

      {/* Testimonials */}
      <section
        {...withElStyle("testimonials-section", { className: "bg-[#fffdf9] py-20 md:py-32 border-y border-[#e5e0d8]" })}
        data-testid="testimonials-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <div className="max-w-2xl mb-12">
            <span className="text-xs tracking-[0.24em] uppercase font-bold text-[#c85a3c]" data-testid="home-testimonials-eyebrow">
              {testiCopy.eyebrow || "Real neighbors, real reviews"}
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-black text-[#2c2a29] leading-[1.05] mt-4" data-testid="home-testimonials-title">
              {testiCopy.title || "4.9 stars across 300+ local homes."}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.slice(0, 6).map((t, i) => (
              <TestimonialCard key={i} item={t} index={i} />
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to={testiCopy.viewAllTo || "/reviews"}
              data-testid="testimonials-view-all"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-[#2c2a29] text-[#2c2a29] hover:bg-[#2c2a29] hover:text-[#fffdf9] font-bold px-7 py-4 rounded-full transition-colors"
            >
              {testiCopy.viewAllLabel || "Read all reviews"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <ServiceArea />

      <CTABanner
        image={IMAGES.finalCta}
        title={finalCta.title || "Ready to get started?"}
        subtitle={finalCta.subtitle || "Free consultations, honest quotes, and a local team that shows up. Let's fix, build, or refresh whatever needs it."}
      />
    </div>
  );
}
